/**
 * No Access Page
 *
 * Shown when a user loses access to their current workspace.
 * This can happen if:
 * - User was removed from the workspace they were viewing
 * - All workspaces were deleted
 * - Webhook created user but skipped workspace (invited users who lost access)
 *
 * The page fetches available workspaces on mount:
 * - If user has other workspaces → shows a list to switch to
 * - If user has zero workspaces → shows create/check invitations options
 */

import { useState, useCallback, useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useClerk } from '@clerk/clerk-react';
import { LogOut, Building2, RefreshCw, Plus, Mail, ArrowRight } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@loader/ui';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@loader/ui';

import { useCreateFirstWorkspace, clearWorkspacesCache } from '@/hooks/useCreateFirstWorkspace';
import { useWorkspaceStore } from '@/store/useWorkspaceStore';
import { useRpc } from '@/lib/rpc';
import { toast } from '@/lib/toast';

interface WorkspaceInfo {
  id: string;
  name: string;
  role?: string;
}

export const Route = createFileRoute('/_authenticated/no-access')({
  component: NoAccessPage,
});

function NoAccessPage() {
  const { signOut } = useClerk();
  const { setSelectedWorkspaceId, clearSelectedWorkspace } = useWorkspaceStore();
  const rpc = useRpc();
  const queryClient = useQueryClient();

  const [availableWorkspaces, setAvailableWorkspaces] = useState<WorkspaceInfo[]>([]);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(true);
  const [isChecking, setIsChecking] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const { createWorkspace: handleCreateWorkspace, isCreating } = useCreateFirstWorkspace({
    onError: (message) => toast.access.createFailed(message),
  });

  // Fetch available workspaces on mount
  useEffect(() => {
    let cancelled = false;

    async function fetchWorkspaces() {
      try {
        // @ts-expect-error - Hono RPC type inference limitation for nested routes
        const res = await rpc.api.private.workspaces.all.$get();
        if (!res.ok || cancelled) return;

        const data = (await res.json()) as {
          workspaces: WorkspaceInfo[];
          last_selected_workspace_id: string | null;
        };

        if (!cancelled) {
          setAvailableWorkspaces(data.workspaces);
        }
      } catch {
        // Silently fail — the page still works without the list
      } finally {
        if (!cancelled) {
          setIsLoadingWorkspaces(false);
        }
      }
    }

    void fetchWorkspaces();
    return () => { cancelled = true; };
  }, [rpc]);

  const handleSwitchWorkspace = useCallback(async (workspaceId: string) => {
    setIsSwitching(true);
    try {
      // Persist selection to database (required for auth middleware)
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces.select.$post({
        json: { workspace_id: workspaceId },
      });

      if (!res.ok) {
        throw new Error('Failed to save workspace selection');
      }

      setSelectedWorkspaceId(workspaceId);
      clearWorkspacesCache(queryClient);
      window.location.href = '/workspace';
    } catch (error) {
      console.error('Failed to switch workspace:', error);
      toast.access.selectFailed();
      setIsSwitching(false);
    }
  }, [rpc, setSelectedWorkspaceId, queryClient]);

  const handleLogout = useCallback(async () => {
    try {
      clearSelectedWorkspace();
      await signOut();
      window.location.href = '/login';
    } catch (error) {
      console.error('Sign out error:', error);
    }
  }, [clearSelectedWorkspace, signOut]);

  const handleCheckForInvitations = useCallback(async () => {
    setIsChecking(true);
    try {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces.all.$get();

      if (res.ok) {
        const data = (await res.json()) as {
          workspaces: WorkspaceInfo[];
          last_selected_workspace_id: string | null;
        };

        if (data.workspaces.length > 0) {
          setAvailableWorkspaces(data.workspaces);
          toast.access.workspaceFound();
        } else {
          toast.access.noInvitations();
        }
      }
    } catch (error) {
      console.error('Failed to check workspaces:', error);
      toast.access.checkFailed();
    } finally {
      setIsChecking(false);
    }
  }, [rpc]);

  const hasWorkspaces = availableWorkspaces.length > 0;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Building2 className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">
            {hasWorkspaces ? 'Access Revoked' : 'Get Started'}
          </CardTitle>
          <CardDescription>
            {hasWorkspaces
              ? 'You no longer have access to the workspace you were using. You can switch to another workspace or create a new one.'
              : "You don't have any workspaces yet. Create one to get started, or check if you have pending invitations."}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-3">
          {/* Available workspaces to switch to */}
          {!isLoadingWorkspaces && hasWorkspaces && (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium text-muted-foreground">Your workspaces</p>
              {availableWorkspaces.map((ws) => (
                <Button
                  key={ws.id}
                  onClick={() => handleSwitchWorkspace(ws.id)}
                  variant="outline"
                  disabled={isSwitching}
                  className="w-full justify-between"
                >
                  <span className="truncate">{ws.name}</span>
                  <ArrowRight className="ml-2 h-4 w-4 shrink-0" />
                </Button>
              ))}
            </div>
          )}

          {isLoadingWorkspaces && (
            <div className="flex items-center justify-center py-2">
              <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Create workspace — only for users with zero workspaces */}
          {!isLoadingWorkspaces && !hasWorkspaces && (
            <Button
              onClick={handleCreateWorkspace}
              disabled={isCreating}
              size="lg"
              className="w-full"
            >
              {isCreating ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Create your workspace
                </>
              )}
            </Button>
          )}

          <Button
            onClick={handleCheckForInvitations}
            variant="outline"
            disabled={isChecking}
            className="w-full"
          >
            {isChecking ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Checking...
              </>
            ) : (
              <>
                <Mail className="mr-2 h-4 w-4" />
                Check for invitations
              </>
            )}
          </Button>

          <p className="mt-2 text-center text-sm text-muted-foreground">
            Waiting for an invitation? Ask your workspace administrator to invite you using your
            email address.
          </p>

          <div className="pt-2">
            <Button onClick={handleLogout} variant="outline" className="w-full">
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
